import React from 'react';
import {View} from 'react-native';
import Modal from 'react-native-modal';
import {Icon} from '@rneui/themed';
import {Formik, FormikConfig} from 'formik';
import * as yup from 'yup';

import { Address } from '../../../models/mfl';
import {useStores} from '../../../stores/use-stores';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import ValidationMessage from '../../themed/ValidationMessage';
import AddressEntry from '../../ui-controls/address/AddressEntry';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

const AddressSchema = yup.object().shape({
  address: yup.object().required('Address is required').nullable(),
});

type Props = {
  children: ({handleOpen}: {handleOpen: () => void}) => React.ReactNode;
  addressType: 'delivery' | 'billing';
}

type Config = {
  title: string;
  placeholder: string;
  formikConfig: FormikConfig<{address: Address}>
}

function AddressModal({
  children,
  addressType,
}: Props) {
  const [isVisible, setIsVisible] = React.useState(false);
  const {cartStore, userStore} = useStores();
  const config: Config = addressType === 'delivery' ? {
    title: 'Delivery address',
    placeholder: 'Delivery address', 
    formikConfig: {
      initialValues: {
        address: cartStore.deliveryAddress,
      },
      onSubmit: ({address}, {setSubmitting}) => {
        setSubmitting(true);
        if (address && cartStore.deliveryAddress !== address) {
          /**
           * have to clear timeslot because no longer valid
           * clear before setDelivery address otherwise it will attempt to validate order
           */
          cartStore.selectDay(null);
          cartStore.selectSlot(null);        
          cartStore.setDeliveryAddress(address);
        }
        setSubmitting(false);
        onClose?.();
      }
    }
  } : {
    title: 'Billing address',
    placeholder: 'Billing address',
    formikConfig: {
      initialValues: {
        address: userStore.billingAddress,
      },
      onSubmit: async ({address}, {setSubmitting, setFieldError}) => {
        setSubmitting(true);
        if (address && userStore.billingAddress !== address) {
          try {
            await userStore.updateBillingAddress(address);
            setSubmitting(false);
            onClose?.();
          } catch (error) {
            setFieldError('address', 'Failed to update billing address. Please try again.');
          }
        } else {
          setSubmitting(false);
          onClose?.();
        }
      }
    }
  }

  const onClose = () => {
    setIsVisible(false);
  };
  const handleOpen = () => setIsVisible(true);

  return (
    <>
      {children({handleOpen})}
      <Modal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}
        style={{padding: 0}}>
        <View
          style={{
            backgroundColor: 'white',
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
            }}>
            <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
                  {config.title}
            </Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>

          <Formik
            initialValues={config.formikConfig.initialValues}
            onSubmit={config.formikConfig.onSubmit}
            validationSchema={AddressSchema}>
            {({
              values,
              handleSubmit,
              isSubmitting,
              setFieldValue,
              errors,
              touched,
            }) => {
              return (
                <View style={{padding: 20}}>
                  <AddressEntry
                    address={values.address}
                    onChange={address => {
                      setFieldValue('address', address);
                    }}
                    placeholder={config.placeholder}
                  />
                  <ValidationMessage>
                    {!!touched.address && errors.address}
                  </ValidationMessage>
                  <View style={{paddingTop: 40}}>
                    <Button
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      onPress={handleSubmit}>
                      SAVE ADDRESS
                    </Button>
                  </View>
                </View>
              );
            }}
          </Formik>
        </View>
      </Modal>
    </>
  );
}

export default AddressModal;
